import { useRef } from "react";

import { Maximize2, Minimize2, Minus, X } from "lucide-react";

/**
 * Props for the ToastHeader component
 *
 * @property {string} title - The title to display in the header
 * @property {string} description - Optional description to display below the title
 * @property {Function} onDismiss - Callback when the dismiss button is clicked (closes the toast)
 * @property {string} dismissMode - "minimise" swaps the close button for a minimise one
 * @property {boolean} isExpanded - Whether the content is expanded
 * @property {Function} onToggleExpand - Callback when the expand/collapse button is clicked
 */
export type ToastHeaderProps = {
  title?: string;
  description?: string;
  onDismiss?: () => void;
  dismissMode?: "close" | "minimise";
  isExpanded: boolean;
  onToggleExpand: () => void;
};

/**
 * Component that displays a header for toast notifications with expand/collapse functionality
 *
 * This component:
 * 1. Displays a title and optional description
 * 2. Shows an X button that calls onDismiss when clicked — or, in "minimise"
 *    mode, a minimise button, hidden once the toast is already minimised
 * 3. Shows an expand/collapse button that toggles the content visibility
 */
export const ToastHeader = ({
  title,
  description,
  onDismiss,
  dismissMode = "close",
  isExpanded,
  onToggleExpand,
}: ToastHeaderProps) => {
  const isMinimiseMode = dismissMode === "minimise";
  // The minimise button is hidden once minimised; hand focus to expand so
  // keyboard users aren't dropped back to the page.
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <div
      className="flex cursor-pointer items-center justify-between gap-6"
      onClick={onToggleExpand}
    >
      <div className="flex-1">
        {/* Live: a title can carry progress (the bundle pop-up's per-stage
            counts), and it's all that shows while minimised. */}
        <h1 className="text-lg font-semibold" aria-live="polite" aria-atomic="true">
          {title}
        </h1>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>

      <div className="flex items-center gap-4">
        <button
          ref={toggleButtonRef}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
          className="text-gray-500 hover:text-gray-700"
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? (
            <Minimize2 className="size-4" />
          ) : (
            <Maximize2 className="size-4" />
          )}
        </button>

        {(!isMinimiseMode || isExpanded) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDismiss?.();
              if (isMinimiseMode) toggleButtonRef.current?.focus();
            }}
            className="text-gray-500 hover:text-gray-700"
            aria-label={isMinimiseMode ? "Minimise" : "Close"}
          >
            {isMinimiseMode ? (
              <Minus className="size-5" />
            ) : (
              <X className="size-5" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};
